import { apiClient, buildQueryParams } from './client';
import {
  Gender,
  Nationality,
  Country,
  City,
  District,
  Category,
  Specialty,
  SubSpecialty,
  ProviderType,
  FloorType,
  AddressType,
  BalanceTransactionType,
  BalanceTransactionChannel,
  PlatformOffice,
  ViolationType,
  PenaltyType,
  TicketCategory,
  TicketSubCategory,
  SpecialtyQuestion,
  SpecialtyQuestionForType,
  SpecialtyQuestionInput,
  SpecialtyQuestionAnswer,
  AppVersion,
  UpdateAppVersionRequest,
  DynamicConfig,
  Provider,
  Order,
  ProviderSpecialty,
  DispatcherPrice,
  SpecialtyAdminDetail,
  CreateSpecialtyRequest,
  UpdateSpecialtyRequest,
  SubCategory,
  CreateSubCategoryRequest,
  UpdateSubCategoryRequest,
  SpecialtyDispatcher,
  CreateDispatcherRequest,
  UpdateDispatcherRequest,
  SpecialtyDistrictCoverage,
  SpecialtyDistrictTransportationPrice,
  SpecialtyDistrictCoverageSubscriber,
  CatalogService,
  CreateCatalogServiceRequest,
  UpdateCatalogServiceRequest,
  CreateSubSpecialtyRequest,
  UpdateSubSpecialtyRequest,
  UserType,
} from '@/types';

// Lookups API - Get all lookup data
export const lookupsApi = {
  // Countries
  getCountries: async (): Promise<Country[]> => {
    const response = await apiClient.get<Country[]>('/lookups/countries');
    return response.data;
  },

  // Genders
  getGenders: async (): Promise<Gender[]> => {
    const response = await apiClient.get<Gender[]>('/lookups/genders');
    return response.data;
  },

  // Nationalities (note: backend has typo "nationalties")
  getNationalities: async (): Promise<Nationality[]> => {
    const response = await apiClient.get<Nationality[]>('/lookups/nationalties');
    return response.data;
  },

  // Cities
  getCities: async (): Promise<City[]> => {
    const response = await apiClient.get<City[]>('/lookups/cities');
    return response.data;
  },

  // Districts by City
  getDistricts: async (cityId: number): Promise<District[]> => {
    const response = await apiClient.get<District[]>('/lookups/districts', { params: { cityId } });
    return response.data;
  },

  // Categories
  getCategories: async (): Promise<Category[]> => {
    const response = await apiClient.get<Category[]>('/catalog/categories');
    return response.data;
  },

  // Specialties
  getSpecialties: async (): Promise<Specialty[]> => {
    const response = await apiClient.get<Specialty[]>('/catalog/specialities');
    return response.data;
  },

  // Specialties by Category
  getSpecialtiesByCategory: async (categoryId: number): Promise<Specialty[]> => {
    const response = await apiClient.get<Specialty[]>(`/catalog/categories/specialities/${categoryId}`);
    return response.data;
  },

  // SubSpecialties
  getSubSpecialties: async (specialtyId: number): Promise<SubSpecialty[]> => {
    const response = await apiClient.get<SubSpecialty[]>(`/catalog/specialities/${specialtyId}/subspecialities`);
    return response.data;
  },

  // Provider Types
  getProviderTypes: async (): Promise<ProviderType[]> => {
    const response = await apiClient.get<ProviderType[]>('/lookups/providertypes');
    return response.data;
  },

  // Floor Types
  getFloorTypes: async (): Promise<FloorType[]> => {
    const response = await apiClient.get<FloorType[]>('/lookups/floortypes');
    return response.data;
  },

  // Address Types
  getAddressTypes: async (): Promise<AddressType[]> => {
    const response = await apiClient.get<AddressType[]>('/lookups/addresstypes');
    return response.data;
  },

  // Balance Transaction Types
  getBalanceTransactionTypes: async (userTypeEnum?: UserType): Promise<BalanceTransactionType[]> => {
    const response = await apiClient.get<BalanceTransactionType[]>('/lookups/balancetransactiontypes', {
      params: userTypeEnum ? { userTypeEnum } : undefined,
    });
    return response.data;
  },

  // Balance Transaction Channels
  getBalanceTransactionChannels: async (): Promise<BalanceTransactionChannel[]> => {
    const response = await apiClient.get<BalanceTransactionChannel[]>('/lookups/balancetransactionchannels');
    return response.data;
  },

  // Platform Offices
  getPlatformOffices: async (): Promise<PlatformOffice[]> => {
    const response = await apiClient.get<PlatformOffice[]>('/lookups/transactions/offices');
    return response.data;
  },

  // Violation Types
  getViolationTypes: async (): Promise<ViolationType[]> => {
    const response = await apiClient.get<ViolationType[]>('/lookups/violationtypes');
    return response.data;
  },

  // Penalty Types
  getPenaltyTypes: async (): Promise<PenaltyType[]> => {
    const response = await apiClient.get<PenaltyType[]>('/lookups/penaltytypes');
    return response.data;
  },

  // Ticket Categories
  getTicketCategories: async (): Promise<TicketCategory[]> => {
    const response = await apiClient.get<TicketCategory[]>('/lookups/ticketcategories');
    return response.data;
  },

  // Ticket SubCategories
  getTicketSubCategories: async (categoryId: number): Promise<TicketSubCategory[]> => {
    const response = await apiClient.get<TicketSubCategory[]>(`/lookups/ticketsubcategories/${categoryId}`);
    return response.data;
  },
};

export interface CountryInput {
  arabicName: string;
  englishName: string;
  isoCode: string;
  callingCode: string;
  currencyCode: string;
  defaultTimezone: string;
  isActive: boolean;
}

export const countriesApi = {
  getAll: async (): Promise<Country[]> => {
    const response = await apiClient.get<Country[]>('/admin/countries');
    return response.data;
  },
  getById: async (countryId: number): Promise<Country> => {
    const response = await apiClient.get<Country>(`/admin/countries/${countryId}`);
    return response.data;
  },
  add: async (data: CountryInput): Promise<number> => {
    const response = await apiClient.post<number>('/admin/countries', data);
    return response.data;
  },
  update: async (countryId: number, data: CountryInput): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/admin/countries/${countryId}`, data);
    return response.data;
  },
  toggleStatus: async (countryId: number, isActive: boolean): Promise<boolean> => {
    const response = await apiClient.patch<boolean>(
      `/admin/countries/${countryId}/status`,
      undefined,
      { params: { isActive } },
    );
    return response.data;
  },
  delete: async (countryId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/admin/countries/${countryId}`);
    return response.data;
  },
};

// Cities API - Full city management via admin endpoints
export const citiesApi = {
  getAll: async (): Promise<City[]> => {
    const response = await apiClient.get<City[]>('/admin/cities');
    return response.data;
  },
  add: async (data: { countryId: number; arabicName: string; englishName: string; isActive: boolean }): Promise<number> => {
    const response = await apiClient.post<number>('/admin/cities', data);
    return response.data;
  },
  update: async (cityId: number, data: { countryId: number; arabicName: string; englishName: string; isActive: boolean }): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/admin/cities/${cityId}`, data);
    return response.data;
  },
  toggleStatus: async (cityId: number, isActive: boolean): Promise<boolean> => {
    const response = await apiClient.patch<boolean>(`/admin/cities/${cityId}/status?isActive=${isActive}`);
    return response.data;
  },
  delete: async (cityId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/admin/cities/${cityId}`);
    return response.data;
  },
  getDistricts: async (cityId: number): Promise<District[]> => {
    const response = await apiClient.get<District[]>(`/admin/cities/${cityId}/districts`);
    return response.data;
  },
  addDistrict: async (cityId: number, data: { arabicName: string; englishName: string }): Promise<number> => {
    const response = await apiClient.post<number>(`/admin/cities/${cityId}/districts`, data);
    return response.data;
  },
  updateDistrict: async (districtId: number, data: { arabicName: string; englishName: string }): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/admin/cities/districts/${districtId}`, data);
    return response.data;
  },
  deleteDistrict: async (districtId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/admin/cities/districts/${districtId}`);
    return response.data;
  },
};

// Categories API
export const categoriesApi = {
  getAll: async (): Promise<Category[]> => {
    const response = await apiClient.get<Category[]>('/admin/categories');
    return response.data;
  },
  getById: async (id: number): Promise<Category> => {
    const response = await apiClient.get<Category>(`/admin/categories/${id}`);
    return response.data;
  },
  create: async (data: Partial<Category>): Promise<number> => {
    const response = await apiClient.post<number>('/admin/categories', data);
    return response.data;
  },
  add: async (data: Partial<Category>): Promise<number> => {
    const response = await apiClient.post<number>('/admin/categories', data);
    return response.data;
  },
  update: async (id: number, data: Partial<Category>): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/admin/categories/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/admin/categories/${id}`);
    return response.data;
  },
};

// SubCategories API (Category -> SubCategory)
export const subCategoriesApi = {
  getAll: async (): Promise<SubCategory[]> => {
    const categories = await categoriesApi.getAll();
    const results = await Promise.all(categories.map((category) => subCategoriesApi.getByCategory(category.id)));
    return results.flat();
  },
  getByCategory: async (categoryId: number): Promise<SubCategory[]> => {
    const response = await apiClient.get<SubCategory[]>(`/admin/categories/${categoryId}/subcategories`);
    return response.data;
  },
  getById: async (id: number): Promise<SubCategory> => {
    const response = await apiClient.get<SubCategory>(`/admin/categories/subcategories/${id}`);
    return response.data;
  },
  create: async (categoryId: number, data: CreateSubCategoryRequest): Promise<number> => {
    const response = await apiClient.post<number>(`/admin/categories/${categoryId}/subcategories`, data);
    return response.data;
  },
  update: async (id: number, data: Omit<UpdateSubCategoryRequest, 'id'> | UpdateSubCategoryRequest): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/admin/categories/subcategories/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/admin/categories/subcategories/${id}`);
    return response.data;
  },
  toggleStatus: async (id: number, isActive: boolean): Promise<boolean> => {
    const response = await apiClient.patch<boolean>(`/admin/categories/subcategories/${id}/status?isActive=${isActive}`);
    return response.data;
  },
};

// Specialties API
export const adminSpecialtiesApi = {
  getAll: async (isActive?: boolean, categoryId?: number): Promise<SpecialtyAdminDetail[]> => {
    const params = buildQueryParams({ isActive, categoryId });
    const response = await apiClient.get<SpecialtyAdminDetail[]>(`/admin/specialties${params}`);
    return response.data;
  },
  getById: async (id: number): Promise<SpecialtyAdminDetail> => {
    const response = await apiClient.get<SpecialtyAdminDetail>(`/admin/specialties/${id}`);
    return response.data;
  },
  create: async (data: CreateSpecialtyRequest): Promise<number> => {
    const response = await apiClient.post<number>('/admin/specialties', data);
    return response.data;
  },
  update: async (id: number, data: UpdateSpecialtyRequest): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/admin/specialties/${id}`, data);
    return response.data;
  },
  toggleStatus: async (id: number, isActive: boolean): Promise<boolean> => {
    const response = await apiClient.patch<boolean>(`/admin/specialties/${id}/status?isActive=${isActive}`);
    return response.data;
  },
};

// SubSpecialties API (Specialty -> SubSpecialty)
export const subSpecialtiesApi = {
  getBySpecialty: async (specialtyId: number): Promise<SubSpecialty[]> => {
    const response = await apiClient.get<SubSpecialty[]>(`/admin/specialties/${specialtyId}/subspecialties`);
    return response.data;
  },
  create: async (specialtyId: number, data: CreateSubSpecialtyRequest): Promise<number> => {
    const response = await apiClient.post<number>(`/admin/specialties/${specialtyId}/subspecialties`, data);
    return response.data;
  },
  update: async (
    id: number,
    data: Partial<Omit<UpdateSubSpecialtyRequest, 'id'>> | UpdateSubSpecialtyRequest,
  ): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/admin/specialties/subspecialties/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/admin/specialties/subspecialties/${id}`);
    return response.data;
  },
  toggleStatus: async (id: number, isActive: boolean): Promise<boolean> => {
    const response = await apiClient.patch<boolean>(`/admin/specialties/subspecialties/${id}/status?isActive=${isActive}`);
    return response.data;
  },
};

// Dispatchers API
export const dispatchersApi = {
  getAll: async (): Promise<SpecialtyDispatcher[]> => {
    const response = await apiClient.get<SpecialtyDispatcher[]>('/admin/specialties/dispatchers');
    return response.data;
  },
  create: async (data: CreateDispatcherRequest): Promise<number> => {
    const response = await apiClient.post<number>('/admin/specialties/dispatchers', data);
    return response.data;
  },
  update: async (id: number, data: UpdateDispatcherRequest): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/admin/specialties/dispatchers/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/admin/specialties/dispatchers/${id}`);
    return response.data;
  },
};

// ... (Keep other APIs like lookups, appVersions, etc.)
export const appVersionsApi = {
  getAll: async (): Promise<AppVersion[]> => {
    const response = await apiClient.get<AppVersion[]>('/lookups/appversions');
    return response.data;
  },
  add: async (data: Omit<AppVersion, 'id' | 'createdOn'>): Promise<boolean> => {
    const response = await apiClient.post<boolean>('/lookups/appversion', data);
    return response.data;
  },
  update: async (id: number, data: UpdateAppVersionRequest): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/lookups/appversion/${id}`, data);
    return response.data;
  },
};

export const dynamicConfigApi = {
  get: async (): Promise<DynamicConfig> => {
    const response = await apiClient.get<DynamicConfig>('/dynamicconfigs');
    return response.data;
  },
  update: async (data: Partial<DynamicConfig>): Promise<DynamicConfig> => {
    const response = await apiClient.post<DynamicConfig>('/dynamicconfigs', data);
    return response.data;
  },
};

export const specialtiesApi = {
  getAll: async (): Promise<SpecialtyAdminDetail[]> => adminSpecialtiesApi.getAll(),
  getById: async (id: number): Promise<SpecialtyAdminDetail> => adminSpecialtiesApi.getById(id),
  create: async (data: CreateSpecialtyRequest): Promise<number> => adminSpecialtiesApi.create(data),
  update: async (id: number, data: UpdateSpecialtyRequest): Promise<boolean> => adminSpecialtiesApi.update(id, data),
  delete: async (id: number): Promise<boolean> => apiClient.delete<boolean>(`/admin/specialties/${id}`).then((response) => response.data),
  toggleStatus: async (id: number, isActive: boolean): Promise<boolean> => adminSpecialtiesApi.toggleStatus(id, isActive),
  getSubSpecialties: async (specialtyId: number): Promise<SubSpecialty[]> => subSpecialtiesApi.getBySpecialty(specialtyId),
  addSubSpecialty: async (
    specialtyId: number,
    data: Omit<CreateSubSpecialtyRequest, 'specialtyId'> | CreateSubSpecialtyRequest,
  ): Promise<number> => subSpecialtiesApi.create(specialtyId, { ...data, specialtyId }),
  updateSubSpecialty: async (
    id: number,
    data: Omit<CreateSubSpecialtyRequest, 'specialtyId'> | CreateSubSpecialtyRequest,
  ): Promise<boolean> => subSpecialtiesApi.update(id, data),
  deleteSubSpecialty: async (id: number): Promise<boolean> => subSpecialtiesApi.delete(id),
  toggleSubSpecialtyStatus: async (id: number, isActive: boolean): Promise<boolean> =>
    subSpecialtiesApi.toggleStatus(id, isActive),
  // These legacy detail sections have no tenant-scoped backend contract and
  // are intentionally hidden. Keep empty compatibility functions while old
  // component markup is removed without issuing requests that always 404.
  getDispatcherPrices: async (specialtyId: number): Promise<DispatcherPrice[]> => {
    void specialtyId;
    return [];
  },
  addDispatcherPrice: async (specialtyId: number, data: { cityId: number; price: number; isActive: boolean }): Promise<number> => {
    void specialtyId;
    void data;
    return Promise.reject(new Error('Dispatcher prices are not supported'));
  },
  updateDispatcherPrice: async (id: number, data: { price: number; isActive: boolean }): Promise<boolean> => {
    void id;
    void data;
    return Promise.reject(new Error('Dispatcher prices are not supported'));
  },
  deleteDispatcherPrice: async (id: number): Promise<boolean> => {
    void id;
    return Promise.reject(new Error('Dispatcher prices are not supported'));
  },
  getProviders: async (specialtyId: number, page = 1): Promise<Provider[]> => {
    void specialtyId;
    void page;
    return [];
  },
  getOrders: async (specialtyId: number, statusId = 0, page = 1): Promise<Order[]> => {
    void specialtyId;
    void statusId;
    void page;
    return [];
  },
  // Repointed to the real SpecialtyQuestions feature. The previous
  // `/admin/specialties/{id}/questions` route does not exist on
  // AdminSpecialtiesController — it 404'd, so the Questions tab never
  // loaded. The backend feature lives at /specialty-questions
  // (GET {specialtyId}, POST, PUT {id}; there is no delete endpoint).
  // The backend GET is split by form type (Customer-order=1,
  // Provider-registration=2, Provider-completion-report=3) and
  // REQUIRES the specialtyQuestionForType query param.
  getQuestions: async (specialtyId: number, forType: SpecialtyQuestionForType = 1): Promise<SpecialtyQuestion[]> =>
    apiClient.get<SpecialtyQuestion[]>(`/specialty-questions/${specialtyId}?specialtyQuestionForType=${forType}`).then((response) => response.data),
  createQuestion: async (data: SpecialtyQuestionInput): Promise<number> =>
    apiClient.post<number>(`/specialty-questions`, data).then((response) => response.data),
  updateQuestion: async (id: number, data: SpecialtyQuestionInput): Promise<boolean> =>
    apiClient.put<boolean>(`/specialty-questions/${id}`, data).then((response) => response.data),
  getAnswers: async (specialtyId: number, userId: string): Promise<SpecialtyQuestionAnswer[]> => {
    const response = await apiClient.get<SpecialtyQuestionAnswer[]>(
      `/supports/specialities/answers/${specialtyId}`,
      { params: { userId, specialtyQuestionForType: 2 } }
    );
    return response.data;
  },
};

export const nationalitiesApi = {
  getAll: async (): Promise<Nationality[]> => {
    const response = await apiClient.get<Nationality[]>('/admin/nationalities');
    return response.data;
  },
  add: async (data: { arabicName: string; englishName: string }): Promise<number> => {
    const response = await apiClient.post<number>('/admin/nationalities', data);
    return response.data;
  },
  update: async (id: number, data: { arabicName: string; englishName: string }): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/admin/nationalities/${id}`, data);
    return response.data;
  },
  delete: async (id: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/admin/nationalities/${id}`);
    return response.data;
  },
};

export const providerSpecialtiesApi = {
  getByProvider: async (userId: string): Promise<ProviderSpecialty[]> => {
    const response = await apiClient.get<ProviderSpecialty[]>(`/providers/specialities${buildQueryParams({ userId })}`);
    return response.data;
  },
  add: async (userId: string, specialtyId: number): Promise<number> => {
    const response = await apiClient.post<number>('/providers/specialities', { userId, specialtyId });
    return response.data;
  },
  remove: async (userId: string, providerSpecialtyId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/providers/specialities/${providerSpecialtyId}${buildQueryParams({ userId })}`);
    return response.data;
  },
  getCatalogSubSpecialties: async (specialtyId: number): Promise<SubSpecialty[]> => {
    const response = await apiClient.get<SubSpecialty[]>(`/providers/subspecialties/catalog${buildQueryParams({ specialtyId })}`);
    return response.data;
  },
  getSubSpecialties: async (userId: string, specialtyId: number): Promise<SubSpecialty[]> => {
    const response = await apiClient.get<SubSpecialty[]>(`/providers/subspecialties${buildQueryParams({ userId, specialtyId })}`);
    return response.data;
  },
  addSubSpecialty: async (userId: string, subSpecialtyId: number): Promise<number> => {
    const response = await apiClient.post<number>('/providers/subspecialties', { userId, subSpecialtyId });
    return response.data;
  },
  removeSubSpecialty: async (userId: string, subSpecialtyId: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/providers/subspecialties/${subSpecialtyId}${buildQueryParams({ userId })}`);
    return response.data;
  },
};

export const specialtyCoverageApi = {
  getBySpecialty: async (specialtyId: number): Promise<SpecialtyDistrictCoverage[]> => {
    const response = await apiClient.get<SpecialtyDistrictCoverage[]>(`/admin/specialties/${specialtyId}/coverage`);
    return response.data;
  },
  // Real backend contract: upsert one district's coverage via
  // PATCH {specialtyId}/coverage/{districtId}?isCovered=. The old
  // POST-by-payload / DELETE-by-id / status-by-id routes did not
  // exist (every coverage mutation 404'd).
  setDistrictCoverage: async (
    specialtyId: number,
    districtId: number,
    isCovered: boolean,
  ): Promise<boolean> => {
    const response = await apiClient.patch<boolean>(
      `/admin/specialties/${specialtyId}/coverage/${districtId}?isCovered=${isCovered}`,
    );
    return response.data;
  },
};

export const specialtyTransportationApi = {
  getBySpecialty: async (specialtyId: number): Promise<SpecialtyDistrictTransportationPrice[]> => {
    const response = await apiClient.get<SpecialtyDistrictTransportationPrice[]>(`/admin/specialties/${specialtyId}/transportation-prices`);
    return response.data;
  },
  // Real backend contract: upsert one district's price via
  // PATCH {specialtyId}/transportation-prices/{districtId}?price=.
  // (Old POST/PUT-by-id/DELETE-by-id routes did not exist.) There is
  // no per-district delete server-side; price 0 = no surcharge.
  setDistrictPrice: async (
    specialtyId: number,
    districtId: number,
    price: number,
  ): Promise<boolean> => {
    const response = await apiClient.patch<boolean>(
      `/admin/specialties/${specialtyId}/transportation-prices/${districtId}?price=${price}`,
    );
    return response.data;
  },
};

export const coverageSubscribersApi = {
  getBySpecialty: async (specialtyId: number): Promise<SpecialtyDistrictCoverageSubscriber[]> => {
    const response = await apiClient.get<SpecialtyDistrictCoverageSubscriber[]>(`/admin/specialties/${specialtyId}/coverage-subscribers`);
    return response.data;
  },
  remove: async (id: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/admin/specialties/coverage-subscribers/${id}`);
    return response.data;
  },
};

export const catalogServicesApi = {
  // subspecialtyId=0 means all subspecialties for this specialty
  getBySpecialty: async (specialtyId: number, subspecialtyId = 0): Promise<CatalogService[]> => {
    const response = await apiClient.get<CatalogService[]>(
      `/services/specialities/${specialtyId}/subspecialities/${subspecialtyId}/services`
    );
    return response.data;
  },
  create: async (data: CreateCatalogServiceRequest): Promise<number> => {
    const response = await apiClient.post<number>('/services', {
      specialtyId: data.specialtyId,
      subSpecialtyId: data.subSpecialtyId || 0,
      name: data.name,
      description: data.description || '',
      price: data.price,
      orderPriority: data.orderPriority || 0,
      imagePath: data.imagePath || '',
      minimumQuantity: data.minimumQuantity,
      maximumQuantity: data.maximumQuantity,
      durationMinutes: data.durationMinutes || null,
      bookingSettings: data.bookingSettings,
      providerUserId: '',
      providerId: 0,
      isActive: data.isActive ?? true,
      serviceType: data.serviceType ?? 0,
      serviceFor: 0,
    });
    return response.data;
  },
  update: async (id: number, data: UpdateCatalogServiceRequest): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/services/${id}`, {
      id,
      specialtyId: data.specialtyId,
      subSpecialtyId: data.subSpecialtyId || 0,
      name: data.name,
      description: data.description || '',
      price: data.price,
      orderPriority: data.orderPriority || 0,
      imagePath: data.imagePath || '',
      minimumQuantity: data.minimumQuantity,
      maximumQuantity: data.maximumQuantity,
      durationMinutes: data.durationMinutes || null,
      bookingSettings: data.bookingSettings,
      providerUserId: '',
      providerId: 0,
      isActive: data.isActive ?? true,
      serviceType: data.serviceType ?? 0,
      serviceFor: 0,
    });
    return response.data;
  },
  delete: async (id: number): Promise<boolean> => {
    const response = await apiClient.delete<boolean>(`/services/${id}`);
    return response.data;
  },
  toggleStatus: async (service: CatalogService): Promise<boolean> => {
    const response = await apiClient.put<boolean>(`/services/${service.id}`, {
      id: service.id,
      specialtyId: service.specialtyId,
      subSpecialtyId: service.subSpecialtyId || 0,
      name: service.name,
      description: service.description || '',
      price: service.price,
      orderPriority: service.orderPriority || 0,
      imagePath: service.imagePath || '',
      minimumQuantity: service.minimumQuantity,
      maximumQuantity: service.maximumQuantity,
      durationMinutes: service.durationMinutes || null,
      providerUserId: '',
      providerId: 0,
      isActive: !service.isActive,
      serviceType: service.serviceType,
      serviceFor: service.serviceFor,
    });
    return response.data;
  },
};
